/**
 * index.ts - API 인프라 통합 설정
 * 
 * Axios 인스턴스와 인터셉터를 설정하고 export합니다.
 */

import { apiClient, publicApiClient, fileApiClient, realtimeApiClient } from './axiosConfig';
import { setupInterceptors } from './interceptors';
import { setupRetryInterceptor } from './retryInterceptor';
import { setupErrorInterceptor, ErrorType } from './errorInterceptor';
import { tokenManager } from './tokenManager';

// 호스트네임에 따른 로그인 경로 결정
const getLoginPath = () => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // localhost, 스테이징 서버에서는 /studyroom prefix 사용
  if (hostname === 'localhost' || hostname === '192.168.0.38' || hostname === '180.70.238.90') {
    // 현재 경로에 따라 적절한 로그인 페이지로 이동
    if (pathname.startsWith('/studyroom/owner') || pathname.startsWith('/owner')) {
      return '/studyroom/owner/login';
    }
    if (pathname.startsWith('/studyroom/parent') || pathname.startsWith('/parent')) {
      return '/studyroom/parent/login';
    }
    if (pathname.startsWith('/studyroom/student') || pathname.startsWith('/student')) {
      return '/studyroom/student/login';
    }
    if (pathname.startsWith('/admin')) {
      return '/admin/login';
    }
    return '/studyroom/owner/login';  // 기본값
  }

  // 운영 환경 (studyroom.newattend.com)
  if (pathname.startsWith('/owner')) {
    return '/owner/login';
  }
  if (pathname.startsWith('/parent')) {
    return '/parent/login';
  }
  if (pathname.startsWith('/student')) {
    return '/student/login';
  }
  if (pathname.startsWith('/admin')) {
    return '/admin/login';
  }
  return '/login';  // 기본값
};

// 인터셉터 옵션
const interceptorOptions = {
  tokenManager,
  logConfig: {
    enabled: process.env.NODE_ENV === 'development',
    logRequest: true,
    logResponse: true,
    logError: true,
  },
  onUnauthorized: () => {
    // 인증 실패 시 적절한 로그인 페이지로 이동
    window.location.href = getLoginPath();
  },
  onNetworkError: (error: any) => {
    console.error('네트워크 오류가 발생했습니다:', error.message);
    // 토스트 알림 등을 표시할 수 있음
  },
};

// 토스트 알림 함수 (실제 구현에서는 토스트 라이브러리 사용)
const showUserMessage = (message: string, type: ErrorType) => {
  console.warn(`[${type}] ${message}`);
  // 실제로는 react-hot-toast, react-toastify 등 사용
};

// 에러 리포팅 함수 (실제 구현에서는 Sentry 등 사용)
const reportError = (errorDetails: any) => {
  if (process.env.NODE_ENV === 'production') {
    console.error('Error reported:', errorDetails);
    // 실제로는 Sentry.captureException(errorDetails.originalError) 등 사용
  }
};

// 각 인스턴스에 인터셉터 설정
setupInterceptors(apiClient, interceptorOptions);
setupRetryInterceptor(apiClient);
setupErrorInterceptor(apiClient, {
  showUserMessage,
  reportError,
});

// publicApiClient는 토큰 관리 제외
setupInterceptors(publicApiClient, {
  ...interceptorOptions,
  tokenManager: undefined,
});
setupRetryInterceptor(publicApiClient);
setupErrorInterceptor(publicApiClient, {
  showUserMessage,
  reportError,
});

// fileApiClient - 파일 업로드는 재시도 비활성화
setupInterceptors(fileApiClient, interceptorOptions);
setupErrorInterceptor(fileApiClient, {
  showUserMessage,
  reportError,
});

// realtimeApiClient - 실시간은 에러 처리만
setupInterceptors(realtimeApiClient, {
  ...interceptorOptions,
  logConfig: {
    ...interceptorOptions.logConfig,
    logRequest: false,
  },
});
setupErrorInterceptor(realtimeApiClient, {
  showUserMessage: (message, type) => {
    // 실시간 통신 에러는 조용히 처리
    console.warn(`[Realtime ${type}] ${message}`);
  },
});

// Export
export { 
  apiClient,
  publicApiClient,
  fileApiClient,
  realtimeApiClient,
} from './axiosConfig';

export { tokenManager } from './tokenManager';

export { 
  isApiError,
  getApiErrorMessage,
  retryConfig,
  type ApiErrorResponse,
} from './axiosConfig';

export { 
  setupInterceptors,
  removeInterceptors,
  type InterceptorOptions,
} from './interceptors';

export {
  setupRetryInterceptor,
  withRetry,
  getRetryCount,
  type RetryConfig,
} from './retryInterceptor';

export {
  setupErrorInterceptor,
  getErrorDetails,
  isRetryableError,
  ErrorType,
  type ErrorDetails,
  type ErrorHandler,
} from './errorInterceptor';